import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DealerCertificateTab from "@/pages/admin/DealerCertificateTab";
import DealerAnalyticsTab from "./dealer/DealerAnalyticsTab";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Archive, ArchiveRestore } from "lucide-react";
import { DealerOverviewTab } from "@/pages/admin/dealer/DealerOverviewTab";
import { DealerUsersTab } from "@/pages/admin/dealer/DealerUsersTab";
import { DealerLocationsTab } from "@/pages/admin/dealer/DealerLocationsTab";
import DealerPricingTab from "@/pages/admin/dealer/DealerPricingTab";

export default function DealerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState(null);
  const [users, setUsers] = useState([]);
  const [showArchivedUsers, setShowArchivedUsers] = useState(false);

  const load = async () => {
    try {
      const [d, u] = await Promise.all([
        api.get(`/dealers/${id}`),
        api.get(`/dealers/${id}/users${showArchivedUsers ? "?include_archived=true" : ""}`),
      ]);
      setDealer(d.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      navigate("/admin/dealers");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, showArchivedUsers]);

  const handleArchiveDealer = async (archive) => {
    try {
      const { data } = await api.post(`/dealers/${id}/${archive ? "archive" : "reactivate"}`);
      if (archive) {
        toast.success("Dealer archived — users can no longer sign in");
      } else if (data?.usable_users === 0) {
        toast.warning("Dealer restored, but no active users can sign in. Restore a user from the Users tab.", { duration: 6000 });
      } else {
        toast.success("Dealer restored");
      }
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!dealer) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="dealer-detail-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div data-testid="dealer-detail-page">
      <Button
        variant="ghost"
        onClick={() => navigate("/admin/dealers")}
        data-testid="back-to-dealers"
        className="mb-6 -ml-3 gap-2 text-warmgrey hover:text-charcoal"
      >
        <ArrowLeft className="h-4 w-4" /> All dealers
      </Button>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {dealer.dealer_code}
          </p>
          <h1 className="mt-2 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">
            {dealer.dba || dealer.legal_name}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {dealer.archived ? (
            <>
              <span className="badge-status badge-neutral" data-testid="dealer-status-badge">archived</span>
              <Button
                variant="outline"
                data-testid="reactivate-dealer-button"
                onClick={() => handleArchiveDealer(false)}
                className="border-limestone text-charcoal hover:bg-sand"
              >
                <ArchiveRestore className="mr-2 h-4 w-4" /> Restore dealer
              </Button>
            </>
          ) : (
            <>
              {dealer.is_test_data && <span className="badge-status badge-reserved" data-testid="dealer-test-badge">TEST</span>}
              <span className={`badge-status ${dealer.status === "active" ? "badge-available" : "badge-neutral"}`} data-testid="dealer-status-badge">
                {dealer.status}
              </span>
              <Button
                variant="outline"
                data-testid="archive-dealer-button"
                onClick={() => handleArchiveDealer(true)}
                className="border-limestone text-status-sold hover:bg-sand"
              >
                <Archive className="mr-2 h-4 w-4" /> Archive dealer
              </Button>
            </>
          )}
        </div>
      </div>

      {dealer.archived && (
        <p className="mt-4 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-4 text-sm text-[#8a6435]" data-testid="dealer-archived-notice">
          This dealer is archived. Its users cannot sign in. Records are preserved for history and audit.
        </p>
      )}

      {dealer.is_test_data && !dealer.archived && (
        <p className="mt-4 rounded-md border border-status-reserved/40 bg-[#D4A373]/10 p-4 text-sm text-[#8a6435]" data-testid="dealer-test-notice">
          Test/Demo dealer — archive keeps history, but test records are meant to be removed permanently: <Link to="/admin/test-data" className="font-medium underline">Admin → Test Data</Link>.
        </p>
      )}

      <Tabs defaultValue="overview" className="mt-10">
        <TabsList className="w-full justify-start overflow-x-auto bg-sand" data-testid="dealer-detail-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview" className="shrink-0 whitespace-nowrap">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users" className="shrink-0 whitespace-nowrap">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-pricing" className="shrink-0 whitespace-nowrap">Pricing</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics" className="shrink-0 whitespace-nowrap">Analytics</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations" className="shrink-0 whitespace-nowrap">
            Locations ({dealer.locations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="certificate" data-testid="tab-certificate" className="shrink-0 whitespace-nowrap">Certificate</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-8">
          <DealerOverviewTab key={dealer.updated_at} dealer={dealer} onSaved={load} />
        </TabsContent>
        <TabsContent value="analytics" className="mt-8">
          <DealerAnalyticsTab dealer={dealer} />
        </TabsContent>
        <TabsContent value="pricing" className="mt-8">
          <DealerPricingTab dealerId={id} />
        </TabsContent>
        <TabsContent value="users" className="mt-8">

          <DealerUsersTab
            dealerId={id}
            users={users}
            showArchived={showArchivedUsers}
            onToggleArchived={setShowArchivedUsers}
            onChanged={load}
            locations={dealer.locations || []}
          />
        </TabsContent>
        <TabsContent value="locations" className="mt-8">
          <DealerLocationsTab dealer={dealer} users={users} onChanged={load} />
        </TabsContent>
        <TabsContent value="certificate" className="mt-8">
          <DealerCertificateTab dealerId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
